<script setup lang="ts">
import { computed, onMounted, ref, useAttrs, useTemplateRef, watch } from "vue";
import {
  isJust,
  just,
  nothing,
  toDomAttr,
  toMaybe,
  type Maybe,
} from "../../lib/maybe";
import ZwField from "./ZwField.vue";
import { usePaintedSelection } from "./usePaintedSelection";

defineOptions({ inheritAttrs: false });

const model = defineModel<string>({ default: "" });

withDefaults(
  defineProps<{
    /** Callers pass `just(text)` or `nothing` explicitly. */
    label: Maybe<string>;
    id: Maybe<string>;
    /** `plain` = borderless inline edit (e.g. a document title). */
    variant?: "default" | "plain";
  }>(),
  {
    variant: "default",
  },
);

/**
 * Contenteditable + painted selection. While a range is selected, the editable
 * goes opacity:0 so WKWebView drops its native selection wash; a face layer
 * shows the text + our --selection rects.
 *
 * `viewText` / `empty` are local mirrors of the editable. `defineModel` only
 * keeps a local value when the parent prop updates; nested v-model into a TEA
 * `shallowRef` often mutates in place and never round-trips, which left the
 * CSS placeholder (::before) stuck on top of typed text.
 */
const attrs = useAttrs();
const input = useTemplateRef<HTMLElement>("input");
const face = useTemplateRef<HTMLElement>("face");
const inputEl = computed(() => toMaybe(input.value));
const { selectionRects, refreshSelectionPaint } = usePaintedSelection(inputEl);

const selecting = computed(() => selectionRects.value.length > 0);
const viewText = ref(model.value === "" ? "" : model.value);
const empty = ref(viewText.value === "");

const placeholder = computed(() => {
  const value = attrs.placeholder;
  return typeof value === "string" ? just(value) : nothing;
});

const disabled = computed(
  () => "disabled" in attrs && attrs.disabled !== false,
);
const readonly = computed(
  () => "readonly" in attrs && attrs.readonly !== false,
);

const editMode = computed(() =>
  disabled.value || readonly.value ? "false" : "plaintext-only",
);

const forwarded = computed(() => {
  const next: Record<string, unknown> = { ...attrs };
  delete next.placeholder;
  delete next.disabled;
  delete next.readonly;
  delete next.autocomplete;
  delete next.writingsuggestions;
  delete next.type;
  delete next.class;
  return next;
});

const shellClass = computed(() => attrs.class);

function readText(el: HTMLElement): string {
  if (el.childNodes.length === 1 && el.firstChild instanceof HTMLBRElement) {
    return "";
  }
  // textContent ignores CSS ::before/::after; innerText can include them.
  return (el.textContent ?? "").replace(/\r?\n/g, "");
}

function writeText(el: HTMLElement, value: string): void {
  const next = value === "" ? "" : value;
  if (readText(el) === next) return;
  el.textContent = next;
}

function commitText(text: string): void {
  viewText.value = text;
  empty.value = text === "";
  model.value = text;
}

function syncFaceScroll(): void {
  const el = input.value;
  const faceEl = face.value;
  if (el === null || faceEl === null) return;
  faceEl.scrollTop = el.scrollTop;
  faceEl.scrollLeft = el.scrollLeft;
}

function onInput(): void {
  const el = input.value;
  if (el === null) return;
  let text = readText(el);
  if ((el.textContent ?? "").includes("\n")) {
    el.textContent = text;
    placeCaretEnd(el);
    text = readText(el);
  }
  commitText(text);
  refreshSelectionPaint();
}

function syncFromEl(): void {
  const el = input.value;
  if (el === null) return;
  commitText(readText(el));
  refreshSelectionPaint();
}

function onPaste(event: ClipboardEvent): void {
  event.preventDefault();
  const text =
    event.clipboardData?.getData("text/plain")?.replace(/\r?\n/g, " ") ?? "";
  document.execCommand("insertText", false, text);
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === "Enter") {
    event.preventDefault();
  }
}

function placeCaretEnd(el: HTMLElement): void {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function selectAll(): void {
  const el = input.value;
  if (el === null) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
  refreshSelectionPaint();
}

watch(model, (value) => {
  const normalized = value === "" ? "" : value;
  viewText.value = normalized;
  empty.value = normalized === "";
  const el = input.value;
  if (el === null) return;
  if (document.activeElement === el && readText(el) === normalized) return;
  writeText(el, normalized);
  refreshSelectionPaint();
});

onMounted(() => {
  const initial = model.value === "" ? "" : model.value;
  viewText.value = initial;
  empty.value = initial === "";
  const el = input.value;
  if (el !== null) {
    writeText(el, initial);
  }
});

defineExpose({
  focus: (options?: FocusOptions) => input.value?.focus(options),
  select: selectAll,
  el: input,
});
</script>

<template>
  <ZwField v-if="isJust(label)" :label="label.value" :for-id="id">
    <div
      class="zw-input-shell"
      :class="[
        shellClass,
        {
          'zw-input-empty': empty,
          'zw-input-selecting': selecting,
          'zw-input-plain-shell': variant === 'plain',
        },
      ]"
    >
      <div ref="face" class="zw-input-face" aria-hidden="true">
        <div class="zw-input-sel-layer">
          <div
            v-for="(r, i) in selectionRects"
            :key="i"
            class="zw-input-sel"
            :style="{
              top: `${r.top}px`,
              left: `${r.left}px`,
              width: `${r.width}px`,
              height: `${r.height}px`,
            }"
          />
        </div>
        <div class="zw-input-face-text">{{ viewText }}</div>
      </div>
      <div
        :id="toDomAttr(id)"
        ref="input"
        class="zw-input"
        :class="{ 'zw-input-plain': variant === 'plain' }"
        role="textbox"
        :aria-label="toDomAttr(label)"
        :contenteditable="editMode"
        :aria-multiline="false"
        :aria-disabled="disabled || undefined"
        :aria-readonly="readonly || undefined"
        :aria-placeholder="toDomAttr(placeholder)"
        :data-placeholder="toDomAttr(placeholder)"
        :tabindex="disabled ? -1 : 0"
        v-bind="forwarded"
        writingsuggestions="false"
        @input="onInput"
        @blur="syncFromEl"
        @compositionend="syncFromEl"
        @paste="onPaste"
        @keydown="onKeydown"
        @keyup="refreshSelectionPaint"
        @mouseup="refreshSelectionPaint"
        @scroll="
          syncFaceScroll();
          refreshSelectionPaint();
        "
      />
    </div>
  </ZwField>
  <div
    v-else
    class="zw-input-shell"
    :class="[
      shellClass,
      {
        'zw-input-empty': empty,
        'zw-input-selecting': selecting,
        'zw-input-plain-shell': variant === 'plain',
      },
    ]"
  >
    <div ref="face" class="zw-input-face" aria-hidden="true">
      <div class="zw-input-sel-layer">
        <div
          v-for="(r, i) in selectionRects"
          :key="i"
          class="zw-input-sel"
          :style="{
            top: `${r.top}px`,
            left: `${r.left}px`,
            width: `${r.width}px`,
            height: `${r.height}px`,
          }"
        />
      </div>
      <div class="zw-input-face-text">{{ viewText }}</div>
    </div>
    <div
      :id="toDomAttr(id)"
      ref="input"
      class="zw-input"
      :class="{ 'zw-input-plain': variant === 'plain' }"
      role="textbox"
      :aria-label="toDomAttr(label)"
      :contenteditable="editMode"
      :aria-multiline="false"
      :aria-disabled="disabled || undefined"
      :aria-readonly="readonly || undefined"
      :aria-placeholder="toDomAttr(placeholder)"
      :data-placeholder="toDomAttr(placeholder)"
      :tabindex="disabled ? -1 : 0"
      v-bind="forwarded"
      writingsuggestions="false"
      @input="onInput"
      @blur="syncFromEl"
      @compositionend="syncFromEl"
      @paste="onPaste"
      @keydown="onKeydown"
      @keyup="refreshSelectionPaint"
      @mouseup="refreshSelectionPaint"
      @scroll="
        syncFaceScroll();
        refreshSelectionPaint();
      "
    />
  </div>
</template>
